import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Key, Shield, ShieldCheck, Smartphone, Copy, Eye, EyeOff, 
  Check, X, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  Lock, QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PasswordStrengthIndicator = ({ password, onChange }) => {
  const [strength, setStrength] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!password || password.length < 3) {
      setStrength(null);
      return;
    }

    const validatePassword = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/validate-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        });
        const data = await response.json();
        setStrength(data);
      } catch (error) {
        console.error('Error validating password:', error);
      }
    };

    const timeout = setTimeout(validatePassword, 300);
    return () => clearTimeout(timeout);
  }, [password]);

  const getStrengthColor = (strengthLabel) => {
    switch (strengthLabel?.toLowerCase()) {
      case 'fraca': return 'bg-red-500';
      case 'média': return 'bg-yellow-500';
      case 'forte': return 'bg-green-500';
      case 'muito forte': return 'bg-emerald-500';
      default: return 'bg-slate-600';
    }
  };

  const getStrengthWidth = (score) => {
    if (!score) return '0%';
    return `${Math.min(100, (score / 7) * 100)}%`;
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Nova senha (mín. 12 caracteres)"
          className="w-full pl-12 pr-12 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 
                   focus:border-[#22c55e]/50 focus:outline-none transition-all"
          minLength={12}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {/* Strength Bar */}
      {password && (
        <div className="space-y-2">
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: getStrengthWidth(strength?.score) }}
              className={`h-full ${getStrengthColor(strength?.strength)} transition-all duration-300`}
            />
          </div>
          
          {strength && (
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${
                strength.strength === 'Fraca' ? 'text-red-400' :
                strength.strength === 'Média' ? 'text-yellow-400' :
                strength.strength === 'Forte' ? 'text-green-400' :
                'text-emerald-400'
              }`}>
                Força: {strength.strength}
              </span>
            </div>
          )}

          {/* Requirements Checklist */}
          {strength?.details && (
            <div className="grid grid-cols-2 gap-2 p-3 bg-white/[0.02] rounded-lg">
              <RequirementItem label="12+ caracteres" met={strength.details.min_length} />
              <RequirementItem label="Letra maiúscula" met={strength.details.uppercase} />
              <RequirementItem label="Letra minúscula" met={strength.details.lowercase} />
              <RequirementItem label="Número" met={strength.details.digit} />
              <RequirementItem label="Caractere especial" met={strength.details.special} />
              <RequirementItem label="Sem palavras proibidas" met={strength.details.no_forbidden} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RequirementItem = ({ label, met }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-emerald-400' : 'text-slate-500'}`}>
    {met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    <span>{label}</span>
  </div>
);

const SecuritySettingsPanel = ({ token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, loading: true });
  const [setup2FA, setSetup2FA] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const getAuthHeader = () => ({ 'Authorization': `Bearer ${token}` });

  useEffect(() => {
    fetch2FAStatus();
  }, [token]);

  const fetch2FAStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/status`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      setTwoFAStatus({ enabled: data.enabled, loading: false });
    } catch (error) {
      setTwoFAStatus({ enabled: false, loading: false });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Senha alterada com sucesso!');
        setCurrentPassword('');
        setNewPassword('');
      } else {
        toast.error(data.detail || 'Erro ao alterar senha');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/generate-password`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      setNewPassword(data.password);
      
      // Copy to clipboard
      navigator.clipboard.writeText(data.password);
      toast.success('Senha gerada e copiada!');
    } catch (error) {
      toast.error('Erro ao gerar senha');
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ password: currentPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSetup2FA(data);
      } else {
        toast.error(data.detail || 'Erro ao configurar 2FA');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ code: verifyCode })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('2FA ativado com sucesso!');
        setTwoFAStatus({ enabled: true, loading: false });
        setShowBackupCodes(true);
      } else {
        toast.error(data.detail || 'Código inválido');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ password: disablePassword, code: disableCode })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('2FA desativado');
        setTwoFAStatus({ enabled: false, loading: false });
        setSetup2FA(null);
        setDisableCode('');
        setDisablePassword('');
      } else {
        toast.error(data.detail || 'Erro ao desativar 2FA');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (setup2FA?.backup_codes) {
      navigator.clipboard.writeText(setup2FA.backup_codes.join('\n'));
      toast.success('Códigos copiados!');
    }
  };

  return (
    <div className="space-y-6">
      {/* Change Password Section */}
      <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Key className="w-5 h-5 text-[#22c55e]" />
          <h3 className="text-white font-semibold">Alterar Senha</h3>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Senha Atual</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Digite sua senha atual"
              required
              className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 
                       focus:border-[#22c55e]/50 focus:outline-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-400 uppercase tracking-wider">Nova Senha</label>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="flex items-center gap-1 text-xs text-[#22c55e] hover:text-[#4ade80]"
              >
                <RefreshCw className="w-3 h-3" />
                Gerar Senha Forte
              </button>
            </div>
            <PasswordStrengthIndicator password={newPassword} onChange={setNewPassword} />
          </div>

          <button
            type="submit"
            disabled={loading || !currentPassword || !newPassword}
            className="w-full px-4 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl
                     hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] disabled:opacity-50
                     flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Key className="w-5 h-5" />}
            Alterar Senha
          </button>
        </form>
      </div>

      {/* 2FA Section */}
      <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#22c55e]" />
            <h3 className="text-white font-semibold">Autenticação em Duas Etapas (2FA)</h3>
          </div>
          {twoFAStatus.loading ? (
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
          ) : (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              twoFAStatus.enabled 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'bg-slate-500/20 text-slate-400'
            }`}>
              {twoFAStatus.enabled ? 'Ativado' : 'Desativado'}
            </span>
          )}
        </div>

        <AnimatePresence mode="wait">
          {!twoFAStatus.enabled ? (
            // Setup 2FA
            <motion.div
              key="setup"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {!setup2FA ? (
                <>
                  <p className="text-slate-400 text-sm">
                    Adicione uma camada extra de segurança à sua conta usando Google Authenticator ou Authy.
                  </p>
                  <div className="space-y-3">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Digite sua senha para continuar"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 
                               focus:border-[#22c55e]/50 focus:outline-none"
                    />
                    <button
                      onClick={handleSetup2FA}
                      disabled={loading || !currentPassword}
                      className="w-full px-4 py-3 bg-blue-500/20 text-blue-400 font-semibold rounded-xl
                               border border-blue-500/30 hover:bg-blue-500/30 disabled:opacity-50
                               flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
                      Configurar 2FA
                    </button>
                  </div>
                </>
              ) : showBackupCodes ? (
                // Backup Codes
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-400 mb-4">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">2FA Ativado com Sucesso!</span>
                  </div>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-400 font-semibold">Códigos de Backup</p>
                        <p className="text-amber-400/70 text-sm">Guarde estes códigos em um local seguro. Cada código pode ser usado apenas uma vez.</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 bg-black/20 rounded-lg p-3 font-mono text-sm">
                      {setup2FA.backup_codes.map((code, i) => (
                        <div key={i} className="text-slate-300">{code}</div>
                      ))}
                    </div>
                    
                    <button
                      onClick={copyBackupCodes}
                      className="mt-3 flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      Copiar códigos
                    </button>
                  </div>
                  
                  <button
                    onClick={() => { setSetup2FA(null); setShowBackupCodes(false); }}
                    className="w-full px-4 py-3 bg-white/[0.05] text-slate-300 rounded-xl border border-white/10 hover:bg-white/[0.08]"
                  >
                    Concluir
                  </button>
                </div>
              ) : (
                // QR Code and Verification
                <div className="space-y-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-white p-4 rounded-xl mb-4">
                      <img 
                        src={`data:image/png;base64,${setup2FA.qr_code}`} 
                        alt="QR Code 2FA" 
                        className="w-48 h-48"
                      />
                    </div>
                    <p className="text-slate-400 text-sm text-center">
                      Escaneie com Google Authenticator ou Authy
                    </p>
                    
                    <div className="mt-3 p-3 bg-white/[0.02] rounded-lg w-full">
                      <p className="text-xs text-slate-500 mb-1">Chave manual:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-slate-300 font-mono break-all">{setup2FA.secret}</code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(setup2FA.secret); toast.success('Copiado!'); }}
                          className="text-slate-400 hover:text-white"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                      Digite o código de 6 dígitos
                    </label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-center text-2xl tracking-[0.5em] font-mono
                               focus:border-[#22c55e]/50 focus:outline-none"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSetup2FA(null)}
                      className="flex-1 px-4 py-3 bg-white/[0.05] text-slate-300 rounded-xl border border-white/10 hover:bg-white/[0.08]"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleVerify2FA}
                      disabled={loading || verifyCode.length !== 6}
                      className="flex-1 px-4 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl
                               hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] disabled:opacity-50
                               flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                      Verificar
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            // Disable 2FA
            <motion.div
              key="disable"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400">Sua conta está protegida com 2FA</span>
              </div>
              
              <div className="pt-4 border-t border-white/[0.05]">
                <p className="text-slate-400 text-sm mb-4">
                  Para desativar o 2FA, confirme sua senha e digite o código do autenticador.
                </p>
                
                <div className="space-y-3">
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Sua senha"
                    className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 
                             focus:border-red-500/50 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="Código do autenticador"
                    maxLength={8}
                    className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 
                             focus:border-red-500/50 focus:outline-none text-center tracking-widest font-mono"
                  />
                  <button
                    onClick={handleDisable2FA}
                    disabled={loading || !disablePassword || !disableCode}
                    className="w-full px-4 py-3 bg-red-500/20 text-red-400 font-semibold rounded-xl
                             border border-red-500/30 hover:bg-red-500/30 disabled:opacity-50
                             flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <X className="w-5 h-5" />}
                    Desativar 2FA
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SecuritySettingsPanel;
